import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import { hasActiveRentalListing } from "@/lib/rental-listing-guard";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";
import {
  aggregateLineTotals,
  type LineTotals,
} from "@/components/rehab/rehab-status";
import type { Strategy } from "@/types/pipeline";

export const REHAB_STAGES = [
  { value: "not_started", label: "Not Started", color: "bg-slate-500" },
  { value: "in_progress", label: "In Progress", color: "bg-blue-500" },
  { value: "completed", label: "Completed", color: "bg-green-500" },
] as const;

export type RehabStage = typeof REHAB_STAGES[number]["value"];

export interface RehabProject {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  created_by_user_id: string;
  stage: RehabStage;
  /**
   * "Actively being rehabbed / editable" flag. true → live in the In Progress
   * tab, drawer editable, AND surfaced on Properties + Finance (the property
   * has entered rehab). false → parked: not-yet-started, or completed (in which
   * case it still shows on Properties as an owned property).
   */
  in_rehab: boolean;
  total_budget: number;
  total_spent: number;
  start_date: string | null;
  target_completion: string | null;
  actual_completion: string | null;
  companycam_project_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** Sum of rehab_line_items.estimated_cost across all rows (loaded with projects) */
  line_estimated_total: number;
  /**
   * Money recognized as spent: sum of rehab_line_items.actual_cost across
   * rows with status = 'completed'. Pending work contributes nothing.
   */
  line_actual_total: number;
  /** Count of budgeted (estimated_cost > 0) line items with status = 'completed' (for the card progress bar). */
  line_completed_count: number;
  /** Count of budgeted (estimated_cost > 0) line items for this project. Zero-cost placeholders are excluded. */
  line_total_count: number;
  // Joined data
  property_data?: any;
  property_address?: string;
  /** pipeline_properties.strategy for this project's property (BRRRR/Flip/Rental). */
  strategy?: Strategy | null;
}

export interface RehabLineItem {
  id: string;
  rehab_project_id: string | null;
  pipeline_property_id: string | null;
  workspace_id: string;
  /** "standard" / "custom" for scope rows; legacy values may exist on older rows. */
  category: string;
  /** Display label for the scope item (preferred over `description`). */
  label: string | null;
  description: string | null;
  estimated_cost: number;
  actual_cost: number;
  contractor_name: string | null;
  status: string;
  /** ISO timestamp the item was marked completed; null while pending. */
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RehabDocument {
  id: string;
  rehab_project_id: string;
  workspace_id: string;
  created_by_user_id: string;
  doc_type: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  file_type: string | null;
  notes: string | null;
  created_at: string;
}

export function useRehab() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [projects, setProjects] = useState<RehabProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // True when a "Start Rehab" was blocked because the property is listed for
  // rent — the Rehab page renders a modal off this flag.
  const [rentalListingBlocked, setRentalListingBlocked] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setIsLoading(true);
    try {
      // Every rehab project is shown here (parked ones render disabled) — the
      // `in_rehab` flag drives editability, not list membership. Completed
      // projects stay on this list (read-only) and also surface on Properties.
      const { data, error } = await supabase
        .from("rehab_projects")
        .select("*")
        .eq("workspace_id", currentWorkspace.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const rows = data || [];
      let lineTotals = new Map<string, LineTotals>();
      if (rows.length > 0) {
        const { data: lineRows, error: lineError } = await supabase
          .from("rehab_line_items")
          .select("rehab_project_id, estimated_cost, actual_cost, status")
          .eq("workspace_id", currentWorkspace.id);
        if (lineError) throw lineError;
        // Budget spans all rows; spend and progress count completed rows only.
        lineTotals = aggregateLineTotals(lineRows);
      }

      // Enrich with property data + strategy from pipeline_properties
      const savedIds = rows.map((p: { pipeline_property_id: string }) => p.pipeline_property_id);
      const propertyMap: Record<string, unknown> = {};
      const strategyMap: Record<string, Strategy | null> = {};
      if (savedIds.length > 0) {
        // `strategy` exists on pipeline_properties but isn't in the generated
        // types yet — cast the builder (same approach as use-owned-properties).
        type SavedPropRow = {
          id: string;
          property_data: unknown;
          strategy: Strategy | null;
        };
        const { data: savedProps } = await (
          supabase as unknown as {
            from: (t: string) => {
              select: (s: string) => {
                in: (
                  col: string,
                  v: string[],
                ) => Promise<{ data: SavedPropRow[] | null }>;
              };
            };
          }
        )
          .from("pipeline_properties")
          .select("id, property_data, strategy")
          .in("id", savedIds);
        (savedProps || []).forEach((sp) => {
          propertyMap[sp.id] = sp.property_data;
          strategyMap[sp.id] = sp.strategy ?? null;
        });
      }

      const enriched: RehabProject[] = rows.map((p: any) => {
        const t = lineTotals.get(p.id) ?? { est: 0, act: 0, done: 0, total: 0 };
        return {
          ...p,
          line_estimated_total: t.est,
          line_actual_total: t.act,
          line_completed_count: t.done,
          line_total_count: t.total,
          property_data: propertyMap[p.pipeline_property_id] || null,
          property_address: (propertyMap[p.pipeline_property_id] as { address?: string } | undefined)
            ?.address || "Unknown",
          strategy: strategyMap[p.pipeline_property_id] ?? null,
        };
      });

      setProjects(enriched);
    } catch (e) {
      console.error("Error fetching rehab projects:", e);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = useCallback(async (savedPropertyId: string) => {
    if (!user?.id || !currentWorkspace?.id) return;
    try {
      // Check if already exists
      const { data: existing } = await supabase
        .from("rehab_projects")
        .select("id")
        .eq("pipeline_property_id", savedPropertyId)
        .eq("workspace_id", currentWorkspace.id)
        .maybeSingle();
      if (existing) {
        toast.info("This property already has a rehab project");
        return existing.id;
      }
      const { data, error } = await supabase
        .from("rehab_projects")
        .insert({
          workspace_id: currentWorkspace.id,
          pipeline_property_id: savedPropertyId,
          created_by_user_id: user.id,
        } as any)
        .select("id")
        .single();
      if (error) throw error;

      // Claim any pre-existing scope rows that were built on the Find
      // detail page (rehab_project_id IS NULL) so they show up under this
      // project in the Rehab drawer immediately. Cast at the boundary —
      // generated supabase types don't yet include pipeline_property_id.
      const newProjectId = data.id;
      const { error: claimError } = await (
        supabase as unknown as {
          from: (t: string) => {
            update: (v: unknown) => {
              eq: (col: string, v: string) => {
                is: (col: string, v: null) => Promise<{ error: unknown }>;
              };
            };
          };
        }
      )
        .from("rehab_line_items")
        .update({ rehab_project_id: newProjectId })
        .eq("pipeline_property_id", savedPropertyId)
        .is("rehab_project_id", null);
      if (claimError) {
        console.error("rehab_line_items claim:", claimError);
      }

      toast.success("Rehab project created");
      await fetchProjects();
      return newProjectId;
    } catch (e) {
      console.error("Error creating rehab project:", e);
      toast.error(toUserMessage(e, "Failed to create rehab project"));
    }
  }, [user?.id, currentWorkspace?.id, fetchProjects]);

  const updateProject = useCallback(async (projectId: string, updates: Partial<RehabProject>) => {
    try {
      const { data, error } = await supabase
        .from("rehab_projects")
        .update(updates as any)
        .eq("id", projectId)
        .select("id");
      if (error) throw error;
      assertRowsAffected(data, "update this rehab project");
      toast.success("Project updated");
      await fetchProjects();
    } catch (e) {
      console.error("Error updating project:", e);
      toast.error(toUserMessage(e, "Failed to update project"));
    }
  }, [fetchProjects]);

  /**
   * Invalidate the property-detail views that read this project's scope.
   * `useDealSettings` (rehab line items) and `usePropertyRehab` (project
   * roll-up) drive the property detail Rehab card's budget/spend figures and
   * progress bar, so a scope edit here must refetch them or the card keeps
   * showing pre-edit numbers. Invalidated by key prefix — the line-item
   * mutations only carry a row id, not the pipeline_property_id — which is
   * cheap: both are small per-property queries and only mounted ones refetch.
   */
  const invalidatePropertyRehabViews = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["deal_settings_rehab_line_items"],
        }),
        queryClient.invalidateQueries({ queryKey: ["property-rehab"] }),
      ]),
    [queryClient],
  );

  // Invalidate the Properties-page queries so a project entering rehab (Start
  // Rehab) or finishing it (Mark Complete) refetches immediately instead of
  // being served stale by TanStack's staleTime.
  const invalidateOwnedProperties = useCallback(() => {
    const wid = currentWorkspace?.id;
    if (!wid) return invalidatePropertyRehabViews();
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ["rehab_projects_finalized", wid] }),
      queryClient.invalidateQueries({ queryKey: ["pipeline_properties_owned", wid] }),
      queryClient.invalidateQueries({ queryKey: ["property_settings_owned", wid] }),
      invalidatePropertyRehabViews(),
    ]);
  }, [queryClient, currentWorkspace?.id, invalidatePropertyRehabViews]);

  /**
   * "Start Rehab" — used both to begin a not-started project and to restart a
   * completed one that was moved to Properties. Pulls the project into active
   * rehab: `in_rehab = true`, `stage = in_progress`, fresh `start_date`, and
   * clears `actual_completion`. Since `in_rehab` flips to true the project
   * leaves the Properties page, so we invalidate those queries too.
   */
  const startProject = useCallback(async (projectId: string) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      // Preserve the original start_date across restarts — only stamp it the
      // first time a project is started (when it's still null). Per-cycle
      // start/finish dates will live in rehab_cycles once that lands; see
      // REHAB_CYCLE_PLAN.md.
      const { data: existing } = await supabase
        .from("rehab_projects")
        .select("start_date, pipeline_property_id, workspace_id")
        .eq("id", projectId)
        .maybeSingle();
      // A property that's actively listed for rent must be removed from
      // Rentals before its rehab can be (re)started.
      const gate = existing as
        | { pipeline_property_id?: string; workspace_id?: string }
        | null;
      if (
        gate?.pipeline_property_id &&
        gate?.workspace_id &&
        (await hasActiveRentalListing(gate.pipeline_property_id, gate.workspace_id))
      ) {
        setRentalListingBlocked(true);
        return;
      }
      const updates: Record<string, unknown> = {
        in_rehab: true,
        stage: "in_progress",
        actual_completion: null,
      };
      if (!(existing as { start_date?: string | null } | null)?.start_date) {
        updates.start_date = today;
      }
      const { data, error } = await supabase
        .from("rehab_projects")
        .update(updates as never)
        .eq("id", projectId)
        .select("id");
      if (error) throw error;
      assertRowsAffected(data, "start this rehab");
      toast.success("Rehab started");
      await Promise.all([fetchProjects(), invalidateOwnedProperties()]);
    } catch (e) {
      console.error("Error starting rehab:", e);
      toast.error(toUserMessage(e, "Failed to start rehab"));
    }
  }, [fetchProjects, invalidateOwnedProperties]);

  /**
   * Mark a project complete. Sets `stage = completed`, flips `in_rehab = false`
   * (rehab is finished — the property settles into the Properties list as an
   * owned property), stamps `actual_completion = today`, and rolls the
   * project's line items up into `total_budget` / `total_spent` so the
   * finalized figures persist.
   */
  const completeProject = useCallback(async (projectId: string) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data: lines, error: linesError } = await supabase
        .from("rehab_line_items")
        .select("estimated_cost, actual_cost")
        .eq("rehab_project_id", projectId);
      if (linesError) throw linesError;
      let estTotal = 0;
      let actTotal = 0;
      for (const l of lines || []) {
        estTotal += Number((l as { estimated_cost: unknown }).estimated_cost) || 0;
        actTotal += Number((l as { actual_cost: unknown }).actual_cost) || 0;
      }
      const { data, error } = await supabase
        .from("rehab_projects")
        .update({
          stage: "completed",
          in_rehab: false,
          actual_completion: today,
          total_budget: estTotal,
          total_spent: actTotal,
        } as never)
        .eq("id", projectId)
        .select("id");
      if (error) throw error;
      assertRowsAffected(data, "complete this rehab");
      toast.success("Rehab completed");
      // `in_rehab` flips false here, so the Properties-page queries must be
      // invalidated alongside the rehab list — otherwise TanStack's staleTime
      // can serve a stale result and the just-completed property looks missing.
      await Promise.all([fetchProjects(), invalidateOwnedProperties()]);
    } catch (e) {
      console.error("Error completing rehab:", e);
      toast.error(toUserMessage(e, "Failed to complete rehab"));
    }
  }, [fetchProjects, invalidateOwnedProperties]);

  // Line items
  const fetchLineItems = useCallback(async (projectId: string): Promise<RehabLineItem[]> => {
    const { data, error } = await supabase
      .from("rehab_line_items")
      .select("*")
      .eq("rehab_project_id", projectId)
      .order("created_at");
    if (error) throw error;
    return (data || []) as unknown as RehabLineItem[];
  }, []);

  const addLineItem = useCallback(async (projectId: string, item: Partial<RehabLineItem>) => {
    if (!currentWorkspace?.id) return;
    try {
      const { error } = await supabase
        .from("rehab_line_items")
        .insert({
          rehab_project_id: projectId,
          // Mirror the project's pipeline_property_id onto the row so it
          // stays addressable by pipeline_property_id later (e.g. the Find
          // detail page reads scope by that key, regardless of project).
          pipeline_property_id: item.pipeline_property_id ?? null,
          workspace_id: currentWorkspace.id,
          category: item.category || "General",
          label: item.label ?? null,
          description: item.description,
          estimated_cost: item.estimated_cost || 0,
          actual_cost: item.actual_cost || 0,
          contractor_name: item.contractor_name,
          status: item.status || "pending",
        } as any);
      if (error) throw error;
      toast.success("Line item added");
      await invalidatePropertyRehabViews();
    } catch (e) {
      console.error("Error adding line item:", e);
      toast.error(toUserMessage(e, "Failed to add line item"));
      throw e;
    }
  }, [currentWorkspace?.id, invalidatePropertyRehabViews]);

  const updateLineItem = useCallback(async (id: string, updates: Partial<RehabLineItem>) => {
    try {
      const { data, error } = await supabase
        .from("rehab_line_items")
        .update(updates as any)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(data, "update this line item");
      await invalidatePropertyRehabViews();
    } catch (e) {
      console.error("Error updating line item:", e);
      toast.error(toUserMessage(e, "Failed to update line item"));
      throw e;
    }
  }, [invalidatePropertyRehabViews]);

  const deleteLineItem = useCallback(async (id: string) => {
    try {
      const { data, error } = await supabase.from("rehab_line_items").delete().eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(data, "delete this line item");
      toast.success("Line item removed");
      await invalidatePropertyRehabViews();
    } catch (e) {
      console.error("Error deleting line item:", e);
      toast.error(toUserMessage(e, "Failed to delete line item"));
      throw e;
    }
  }, [invalidatePropertyRehabViews]);

  // Documents
  const fetchDocuments = useCallback(async (projectId: string): Promise<RehabDocument[]> => {
    const { data, error } = await supabase
      .from("rehab_documents")
      .select("*")
      .eq("rehab_project_id", projectId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data || []) as unknown as RehabDocument[];
  }, []);

  const uploadDocument = useCallback(async (projectId: string, file: File, docType: string) => {
    if (!user?.id || !currentWorkspace?.id) return;
    try {
      const filePath = `${currentWorkspace.id}/${projectId}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("rehab-documents")
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("rehab-documents")
        .getPublicUrl(filePath);

      const { error } = await supabase.from("rehab_documents").insert({
        rehab_project_id: projectId,
        workspace_id: currentWorkspace.id,
        created_by_user_id: user.id,
        doc_type: docType,
        file_name: file.name,
        file_url: urlData.publicUrl,
        file_size: file.size,
        file_type: file.type,
      } as any);
      if (error) throw error;
      toast.success("Document uploaded");
    } catch (e) {
      console.error("Error uploading document:", e);
      toast.error(toUserMessage(e, "Failed to upload document"));
      throw e;
    }
  }, [user?.id, currentWorkspace?.id]);

  const deleteDocument = useCallback(async (doc: RehabDocument) => {
    try {
      // Extract path from URL
      const pathMatch = doc.file_url.match(/rehab-documents\/(.+)/);
      if (pathMatch) {
        await supabase.storage.from("rehab-documents").remove([pathMatch[1]]);
      }
      const { data, error } = await supabase.from("rehab_documents").delete().eq("id", doc.id).select("id");
      if (error) throw error;
      assertRowsAffected(data, "delete this document");
      toast.success("Document deleted");
    } catch (e) {
      console.error("Error deleting document:", e);
      toast.error(toUserMessage(e, "Failed to delete document"));
      throw e;
    }
  }, []);

  // CompanyCam
  const createCompanyCamProject = useCallback(async (project: RehabProject) => {
    if (!project.property_data || !currentWorkspace?.id) return;
    const pd = project.property_data;
    const { data, error } = await supabase.functions.invoke("companycam", {
      body: {
        action: "create_project",
        workspace_id: currentWorkspace.id,
        name: `Rehab: ${pd.address}`,
        address: pd.address,
        city: pd.city,
        state: pd.state,
        zip: pd.zip,
      },
    });
    if (error) { toast.error(data?.error || "Failed to create CompanyCam project"); return; }
    if (data?.id) {
      await updateProject(project.id, { companycam_project_id: data.id } as any);
      toast.success("CompanyCam project created & linked");
      return data.id;
    }
  }, [updateProject, currentWorkspace?.id]);

  const fetchCompanyCamPhotos = useCallback(async (companycamProjectId: string) => {
    if (!currentWorkspace?.id) return [];
    const { data } = await supabase.functions.invoke("companycam", {
      body: { action: "get_photos", workspace_id: currentWorkspace.id, project_id: companycamProjectId },
    });
    return data || [];
  }, [currentWorkspace?.id]);

  const inviteContractorToCompanyCam = useCallback(async (companycamProjectId: string, email: string) => {
    if (!currentWorkspace?.id) return;
    const { data } = await supabase.functions.invoke("companycam", {
      body: {
        action: "invite_collaborator",
        workspace_id: currentWorkspace.id,
        project_id: companycamProjectId,
        email,
      },
    });
    if (data) toast.success("Contractor invited to CompanyCam");
    return data;
  }, [currentWorkspace?.id]);

  return {
    projects,
    isLoading,
    refetch: fetchProjects,
    /** True when the last "Start Rehab" was blocked by an active rental listing. */
    rentalListingBlocked,
    setRentalListingBlocked,
    createProject,
    updateProject,
    startProject,
    completeProject,
    fetchLineItems,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    createCompanyCamProject,
    fetchCompanyCamPhotos,
    inviteContractorToCompanyCam,
  };
}
